import {
  Box,
  Flex,
  HStack,
  IconButtonWithTooltip,
  Input,
  InputGroup,
  InputLeftElement,
  PlusIcon,
  Spinner,
  Text,
} from '@elkaso-app/web-design';
import { useGetDealCategoryTreeApi } from 'apis/exclusive-deals';
import { useInfiniteMasterProductFamiliesApi } from 'apis/master-product-families';
import { useDebounce } from 'hooks/use-debounce';
import { usePageParams } from 'hooks/use-page-params';
import { findCategoryPath } from 'pages/exclusive-deals/components/editable-deals-grid/category-tree-helpers';
import { InlineFamilyForm } from 'pages/inquiry-processor/components/item-board/inline-family-form';
import { FamilyCategoryFilterButton } from 'pages/inquiry-processor/components/master-product-families/filters/category-filter';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FiSearch } from 'react-icons/fi';

export interface FamilyListPanelProps {
  selectedFamilyId?: number;
  onSelect: (familyId: number) => void;
}

// Left pane per the design handoff: rounded search field + category filter,
// uppercase count label, and family rows as rounded cards with a tinted
// selected state. The list is infinite-scrolled — a sentinel at the bottom of
// the scroll container fetches the next page as it comes into view. Search
// (debounced) and category still live in the URL (`universalName`,
// `catalogCategoryId`); paging is internal to the infinite query.
export const FamilyListPanel = ({ selectedFamilyId, onSelect }: FamilyListPanelProps) => {
  const { getPageParams, setPageParams } = usePageParams();
  const { universalName } = getPageParams();

  const [searchInput, setSearchInput] = useState((universalName as string) || '');
  const debouncedSearch = useDebounce(searchInput, 300);
  // Inline "new master item" form under the search box (seeded with the
  // search text — a family you couldn't find is usually the one to create).
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } = useInfiniteMasterProductFamiliesApi();
  const families = useMemo(() => (data?.pages ?? []).flatMap((page) => page.data), [data]);
  const total = data?.pages?.[0]?.total ?? 0;

  const { data: categoryTreeData } = useGetDealCategoryTreeApi();
  const categoryTree = categoryTreeData?.data ?? [];

  // Keep the input's local echo in sync when the URL param changes from
  // elsewhere (e.g. browser back/forward).
  useEffect(() => {
    setSearchInput((universalName as string) || '');
  }, [universalName]);

  useEffect(() => {
    const next = getPageParams();
    if (((next.universalName as string | undefined) ?? '') === debouncedSearch) return;
    delete next.page;
    setPageParams({ ...next, universalName: debouncedSearch || undefined });
  }, [debouncedSearch]);

  // Infinite-scroll sentinel: fetch the next page when it scrolls into view.
  const scrollBoxRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) fetchNextPage();
      },
      { root: scrollBoxRef.current, rootMargin: '200px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const categoryLabel = (catalogCategoryId: number) => {
    const path = findCategoryPath(categoryTree, catalogCategoryId);
    return path.length ? path.map((n) => n.title || `Untitled (${n.id})`).join(' › ') : `#${catalogCategoryId}`;
  };

  return (
    <Flex
      direction='column'
      w={{ base: 'full', md: '290px' }}
      flexShrink={0}
      borderRight={{ base: 'none', md: '1px solid' }}
      borderRightColor='gray.200'>
      <Box p='16px 16px 12px'>
        <HStack spacing='8px' align='center'>
          <InputGroup>
            <InputLeftElement pointerEvents='none' h='full'>
              <FiSearch color='var(--chakra-colors-gray-400)' size='15px' />
            </InputLeftElement>
            <Input
              size='sm'
              borderRadius='10px'
              bg='#FAFAFB'
              fontSize='13px'
              placeholder='Search families…'
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </InputGroup>
          <FamilyCategoryFilterButton />
          <IconButtonWithTooltip
            size='sm'
            label='New master item'
            aria-label='new-family-btn'
            Icon={<PlusIcon />}
            variant='unstyled'
            onClick={() => setIsCreateOpen((prev) => !prev)}
          />
        </HStack>
        {isCreateOpen && (
          <Box mt='10px'>
            <InlineFamilyForm
              initialName={searchInput}
              onCancel={() => setIsCreateOpen(false)}
              onCreated={(family) => {
                setIsCreateOpen(false);
                onSelect(family.id);
              }}
            />
          </Box>
        )}
        <Text
          mt='9px'
          fontSize='10.5px'
          fontWeight='semibold'
          letterSpacing='0.09em'
          color='gray.500'
          textTransform='uppercase'>
          {isLoading ? 'Loading…' : `${families.length} of ${total} families`}
        </Text>
      </Box>

      <Box ref={scrollBoxRef} flex='1' overflowY='auto' px='10px' pb='16px'>
        {isLoading ? (
          <Flex justify='center' py='lg'>
            <Spinner size='sm' color='red.500' />
          </Flex>
        ) : families.length ? (
          families.map((family) => {
            const isSelected = family.id === selectedFamilyId;

            return (
              <Box
                key={family.id}
                as='button'
                display='block'
                w='full'
                textAlign='start'
                px='12px'
                py='10px'
                mb='4px'
                borderRadius='9px'
                border='1px solid'
                borderColor={isSelected ? 'red.100' : 'transparent'}
                bg={isSelected ? 'red.50' : 'transparent'}
                _hover={{ bg: isSelected ? 'red.50' : 'gray.50' }}
                onClick={() => onSelect(family.id)}>
                <Text
                  fontSize='13.5px'
                  fontWeight={isSelected ? 'semibold' : 'medium'}
                  color={isSelected ? 'red.600' : 'gray.800'}
                  noOfLines={1}>
                  {family.universalName}
                </Text>
                <Text fontSize='11.5px' color='gray.500' mt='2px' noOfLines={1}>
                  {categoryLabel(family.catalogCategoryId)}
                </Text>
              </Box>
            );
          })
        ) : (
          <Text fontSize='13px' color='gray.500' textAlign='center' py='30px'>
            No families found
          </Text>
        )}

        <Box ref={sentinelRef} h='1px' />
        {isFetchingNextPage && (
          <Flex justify='center' py='sm'>
            <Spinner size='xs' color='red.500' />
          </Flex>
        )}
      </Box>
    </Flex>
  );
};
